import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  CheckCircle2,
  ExternalLink,
  PlayCircle,
  RefreshCcw,
  XCircle,
} from "lucide-react";

import { toast } from "@/hooks/use-toast";
import { confirmDialog } from "@/components/ui/confirm-dialog";

import { AppSidebar } from "@/components/sidebar/app-sidebar";
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  BackupVerifyLatestPerCluster,
  BackupVerifyRun,
  getLatestBackupVerifyPerCluster,
  listBackupVerifyRuns,
  triggerBackupVerify,
} from "@/services/backupVerifyApi";

// Liste des clusters pour lesquels on peut déclencher manuellement un run.
// Doit matcher CLUSTER_CRONJOBS côté backoffice services.py.
const TRIGGERABLE_CLUSTERS = new Set([
  "n8n-postgres-cluster",
  "recrutauto-postgres-cluster",
]);

function formatBytes(bytes: number | null): string {
  if (!bytes || bytes <= 0) return "—";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let v = bytes;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(v < 10 ? 1 : 0)} ${units[i]}`;
}

function formatDuration(startedAt: string, finishedAt: string): string {
  const ms = new Date(finishedAt).getTime() - new Date(startedAt).getTime();
  if (ms < 0) return "—";
  if (ms < 1000) return `${ms}ms`;
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  return `${m}m${s % 60}s`;
}

function ageColor(iso: string | undefined): string {
  if (!iso) return "text-muted-foreground";
  const days = (Date.now() - new Date(iso).getTime()) / (1000 * 60 * 60 * 24);
  if (days > 8) return "text-red-600 font-semibold";
  if (days > 7) return "text-orange-600";
  return "text-emerald-600";
}

const grafanaUrl = (() => {
  // Détecte l'env via le hostname du window (admin.dev.recrutauto.fr → dev).
  const host = typeof window !== "undefined" ? window.location.host : "";
  const env = host.includes("dev.") ? ".dev" : "";
  return `https://grafana.internal${env}.recrutauto.fr/d/backup-health`;
})();

export default function BackupVerifyPage() {
  const navigate = useNavigate();

  const [latest, setLatest] = useState<BackupVerifyLatestPerCluster[]>([]);
  const [runs, setRuns] = useState<BackupVerifyRun[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedRun, setSelectedRun] = useState<BackupVerifyRun | null>(null);
  const [triggering, setTriggering] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [latestData, runsData] = await Promise.all([
        getLatestBackupVerifyPerCluster(navigate),
        listBackupVerifyRuns(navigate, { limit: 100 }),
      ]);
      setLatest(latestData);
      setRuns(runsData.items);
      setTotal(runsData.total);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erreur inconnue");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleTrigger = useCallback(
    async (cluster: string) => {
      const ok = await confirmDialog({
        title: `Forcer un run de verify ?`,
        description: (
          <>
            Cette action va créer un Job Kubernetes ad-hoc qui télécharge le
            dernier base backup de <code>{cluster}</code> dans un PVC scratch
            et lance <code>pg_controldata</code>. <br />
            Coût : ~1 min, ~50 MB-1 GB de bande passante S3 lue (selon la
            taille DB), zéro impact sur la base en production.
          </>
        ),
        confirmLabel: "Lancer",
      });
      if (!ok) return;
      setTriggering(cluster);
      try {
        const res = await triggerBackupVerify(navigate, cluster);
        toast({
          title: "Run déclenché",
          description: `Job ${res.job_name} créé dans ${res.namespace}/. Le résultat apparaîtra dans le journal d'ici 1-2 min.`,
        });
        // Refresh dans 30s pour laisser le temps au Job de pousser.
        setTimeout(() => refresh(), 30_000);
      } catch (e) {
        toast({
          title: "Echec du déclenchement",
          description: e instanceof Error ? e.message : "Erreur inconnue",
          variant: "destructive",
        });
      } finally {
        setTriggering(null);
      }
    },
    [navigate, refresh],
  );

  const groupedByCluster = useMemo(() => {
    // Union des clusters connus (au moins 1 run) et de ceux qu'on peut
    // déclencher manuellement → on affiche une carte même quand le cluster
    // n'a jamais tourné, pour permettre le premier trigger.
    const known = new Set([
      ...latest.map((l) => l.cluster),
      ...TRIGGERABLE_CLUSTERS,
    ]);
    return Array.from(known).sort();
  }, [latest]);

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <header className="flex h-16 shrink-0 items-center gap-2 border-b px-4">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="mr-2 h-4" />
          <h1 className="text-lg font-semibold">Verifications de backup</h1>
          <div className="ml-auto flex items-center gap-2">
            <Button variant="outline" size="sm" asChild>
              <a href={grafanaUrl} target="_blank" rel="noreferrer">
                Grafana <ExternalLink className="ml-1 h-3 w-3" />
              </a>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={refresh}
              disabled={loading}
            >
              <RefreshCcw className={`mr-1 h-3 w-3 ${loading ? "animate-spin" : ""}`} />
              Rafraichir
            </Button>
          </div>
        </header>

        <main className="flex flex-col gap-6 p-6">
          {error && (
            <div className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-800">
              {error}
            </div>
          )}

          <section>
            <h2 className="mb-3 text-base font-medium">Dernier verify par cluster</h2>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              {latest.length === 0 && !loading && (
                <Card className="md:col-span-3">
                  <CardContent className="p-6 text-center text-muted-foreground">
                    Aucun run enregistre pour l'instant. Le CronJob hebdo (dim 03:30 UTC pour
                    n8n, 04:00 UTC pour recrutauto) renseignera ces cartes au premier passage.
                  </CardContent>
                </Card>
              )}
              {groupedByCluster.map((cluster) => {
                const entry = latest.find((l) => l.cluster === cluster);
                const lastRun = entry?.last_run ?? null;
                const lastSuccess = entry?.last_success ?? null;
                const isHealthy = lastRun?.status === "success";
                const canTrigger = TRIGGERABLE_CLUSTERS.has(cluster);
                const isTriggering = triggering === cluster;
                return (
                  <Card key={cluster}>
                    <CardHeader>
                      <CardTitle className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2">
                          {lastRun ? (
                            isHealthy ? (
                              <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                            ) : (
                              <XCircle className="h-4 w-4 text-red-600" />
                            )
                          ) : (
                            <AlertTriangle className="h-4 w-4 text-orange-500" />
                          )}
                          {cluster}
                        </div>
                        {canTrigger && (
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={isTriggering || loading}
                            onClick={() => handleTrigger(cluster)}
                          >
                            <PlayCircle
                              className={`mr-1 h-3 w-3 ${
                                isTriggering ? "animate-spin" : ""
                              }`}
                            />
                            {isTriggering ? "Lancement..." : "Forcer un run"}
                          </Button>
                        )}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2 text-sm">
                      <div>
                        <span className="text-muted-foreground">Dernier run :</span>{" "}
                        {lastRun ? (
                          <>
                            <Badge
                              variant={isHealthy ? "default" : "destructive"}
                              className="ml-1"
                            >
                              {lastRun.status}
                            </Badge>{" "}
                            <span className={ageColor(lastRun.started_at)}>
                              {new Date(lastRun.started_at).toLocaleString()}
                            </span>
                          </>
                        ) : (
                          <span className="text-muted-foreground">jamais</span>
                        )}
                      </div>
                      <div>
                        <span className="text-muted-foreground">Dernier succes :</span>{" "}
                        {lastSuccess ? (
                          <span className={ageColor(lastSuccess.started_at)}>
                            {new Date(lastSuccess.started_at).toLocaleString()}
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-orange-600">
                            <AlertTriangle className="h-3 w-3" /> aucun
                          </span>
                        )}
                      </div>
                      <div className="text-muted-foreground">
                        Backup : {lastRun?.backup_name ?? "—"} ({formatBytes(lastRun?.bytes_size ?? null)})
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </section>

          <section>
            <div className="mb-3 flex items-center gap-2">
              <h2 className="text-base font-medium">Historique</h2>
              <Badge variant="secondary">{total} runs</Badge>
            </div>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Cluster</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Demarre</TableHead>
                    <TableHead>Duree</TableHead>
                    <TableHead>Backup</TableHead>
                    <TableHead>Taille</TableHead>
                    <TableHead>Job</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {runs.map((r) => (
                    <TableRow
                      key={r.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => setSelectedRun(r)}
                    >
                      <TableCell className="font-mono text-xs">{r.cluster}</TableCell>
                      <TableCell>
                        <Badge
                          variant={r.status === "success" ? "default" : "destructive"}
                        >
                          {r.status}
                        </Badge>
                      </TableCell>
                      <TableCell>{new Date(r.started_at).toLocaleString()}</TableCell>
                      <TableCell>{formatDuration(r.started_at, r.finished_at)}</TableCell>
                      <TableCell className="font-mono text-xs">
                        {r.backup_name ?? "—"}
                      </TableCell>
                      <TableCell>{formatBytes(r.bytes_size)}</TableCell>
                      <TableCell className="font-mono text-xs">{r.job_name}</TableCell>
                    </TableRow>
                  ))}
                  {runs.length === 0 && !loading && (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center text-muted-foreground">
                        Aucun run enregistre.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </section>
        </main>

        <Dialog
          open={!!selectedRun}
          onOpenChange={(open) => !open && setSelectedRun(null)}
        >
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle>
                {selectedRun?.cluster} — {selectedRun?.status}
              </DialogTitle>
            </DialogHeader>
            {selectedRun && (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-muted-foreground">Job :</span>{" "}
                    <span className="font-mono">{selectedRun.job_name}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Namespace :</span>{" "}
                    {selectedRun.namespace}
                  </div>
                  <div>
                    <span className="text-muted-foreground">Demarre :</span>{" "}
                    {new Date(selectedRun.started_at).toLocaleString()}
                  </div>
                  <div>
                    <span className="text-muted-foreground">Duree :</span>{" "}
                    {formatDuration(selectedRun.started_at, selectedRun.finished_at)}
                  </div>
                  <div>
                    <span className="text-muted-foreground">Backup :</span>{" "}
                    <span className="font-mono">{selectedRun.backup_name ?? "—"}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Taille :</span>{" "}
                    {formatBytes(selectedRun.bytes_size)}
                  </div>
                </div>
                <div>
                  <div className="mb-1 text-sm font-medium">Logs (tail) :</div>
                  <pre className="max-h-96 overflow-auto rounded-md bg-muted p-3 text-xs font-mono">
                    {selectedRun.log_tail || "(vide)"}
                  </pre>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </SidebarInset>
    </SidebarProvider>
  );
}
